import { create } from 'zustand';

export type CuOStage =
  | 'setup'
  | 'tube-weighed'
  | 'cuo-added'
  | 'cuo-weighed'
  | 'apparatus-built'
  | 'connected'
  | 'purging'
  | 'heating'
  | 'reducing'
  | 'cooling'
  | 'dried'
  | 'final-weighed'
  | 'constant-mass'
  | 'observed'
  | 'completed';

export type TrialId = 1 | 2 | 3;
export type CameraView = 'default' | 'top' | 'side' | 'closeup' | 'workbench';
export type WeighType = 'empty' | 'with-cuo' | 'final';

export interface TrialPreset {
  id: TrialId;
  cuoMassG: number;
}

export const TRIAL_PRESETS: Record<TrialId, TrialPreset> = {
  1: { id: 1, cuoMassG: 0.80 },
  2: { id: 2, cuoMassG: 1.60 },
  3: { id: 3, cuoMassG: 2.40 },
};

export interface TrialResult {
  trialId: TrialId;
  emptyTubeMassG: number;
  tubeWithCuOMassG: number;
  tubeWithCuMassG: number;
  cuOMassG: number;
  cuMassG: number;
  oxygenLostG: number;
  molesCu: number;
  molesO: number;
  ratioCuToO: number;
  empiricalFormula: string;
  recordedAt: number;
}

interface CuOState {
  currentTrial: TrialId;
  labStage: CuOStage;

  emptyTubeMassG: number;
  cuOInTubeG: number;
  cuInTubeG: number;
  reductionProgress: number;
  currentReadingG: number;
  targetReadingG: number;

  isHeating: boolean;
  flameIntensity: number;
  tubeTempC: number;

  isH2Flowing: boolean;
  purgeSeconds: number;
  h2StoppedEarlyG: number;

  reheatCycle: number;
  lastWeighingG: number;

  // Apparatus visibility flags
  waterAdded: boolean;
  znAdded: boolean;
  hclAdded: boolean;
  apparatusBuilt: boolean;
  connected: boolean;
  cottonApplied: boolean;

  // Animation flags / progress
  isWeighing: boolean;
  weighProgress: number;
  weighType: WeighType | null;
  isPlacingCuO: boolean;
  placeCuOProgress: number;
  isBuildingApparatus: boolean;
  buildProgress: number;
  isConnecting: boolean;
  connectProgress: number;
  isApplyingCottonBud: boolean;
  cottonProgress: number;
  isReheating: boolean;
  reheatProgress: number;

  experimentTime: number;
  results: TrialResult[];

  simulationSpeed: number;
  cameraView: CameraView;
  cameraVersion: number;
  showTutorial: boolean;

  // Actions
  selectTrial: (id: TrialId) => void;
  weighEmptyTube: () => void;
  addCuOToTube: () => void;
  weighTubeWithCuO: () => void;
  buildApparatus: () => void;
  connectApparatus: () => void;
  startH2Flow: () => void;
  toggleH2Flow: () => void;
  toggleHeating: () => void;
  setFlameIntensity: (i: number) => void;
  applyCottonBud: () => void;
  weighFinal: () => void;
  checkConstantMass: () => void;
  recordObservation: () => void;
  resetTrial: () => void;
  resetAll: () => void;
  setSimulationSpeed: (s: number) => void;
  cycleSimulationSpeed: () => void;
  setCameraView: (v: CameraView) => void;
  setShowTutorial: (show: boolean) => void;
  tick: (delta: number) => void;
}

const SPEED_CYCLE = [1, 2, 5, 10];
const EMPTY_TUBE_MASS_G = 5.00;
const PURGE_REQUIRED_SECONDS = 10;
const REDUCTION_DURATION = 6.0;
const WEIGH_DURATION = 3.0;
const PLACE_CUO_DURATION = 1.6;
const BUILD_DURATION = 5.0;
const CONNECT_DURATION = 2.0;
const COTTON_DURATION = 1.5;
const REHEAT_DURATION = 5.0;
const REOXIDATION_RATE_G_PER_S = 0.012;
const REOXIDATION_CAP_G = 0.18;
const IGNITION_TEMP_C = 250;
const TUBE_MAX_HEAT_C = 720;

function buildInitialTrialState(id: TrialId) {
  return {
    currentTrial: id,
    labStage: 'setup' as CuOStage,
    emptyTubeMassG: EMPTY_TUBE_MASS_G,
    cuOInTubeG: 0,
    cuInTubeG: 0,
    reductionProgress: 0,
    currentReadingG: 0,
    targetReadingG: 0,
    isHeating: false,
    flameIntensity: 0.9,
    tubeTempC: 25,
    isH2Flowing: false,
    purgeSeconds: 0,
    h2StoppedEarlyG: 0,
    reheatCycle: 0,
    lastWeighingG: 0,
    waterAdded: false,
    znAdded: false,
    hclAdded: false,
    apparatusBuilt: false,
    connected: false,
    cottonApplied: false,
    isWeighing: false,
    weighProgress: 0,
    weighType: null as WeighType | null,
    isPlacingCuO: false,
    placeCuOProgress: 0,
    isBuildingApparatus: false,
    buildProgress: 0,
    isConnecting: false,
    connectProgress: 0,
    isApplyingCottonBud: false,
    cottonProgress: 0,
    isReheating: false,
    reheatProgress: 0,
    experimentTime: 0,
  };
}

export const useCopperOxideStore = create<CuOState>((set, get) => ({
  ...buildInitialTrialState(1),
  results: [],
  simulationSpeed: 1,
  cameraView: 'default',
  cameraVersion: 0,
  showTutorial: false,

  selectTrial: (id) => {
    set({ ...buildInitialTrialState(id) });
  },

  weighEmptyTube: () => set((s) => {
    if (s.labStage !== 'setup' || s.isWeighing) return {};
    return {
      isWeighing: true,
      weighProgress: 0,
      weighType: 'empty',
      targetReadingG: 0,
    };
  }),

  addCuOToTube: () => set((s) => {
    if (s.labStage !== 'tube-weighed' || s.isPlacingCuO) return {};
    return { isPlacingCuO: true, placeCuOProgress: 0 };
  }),

  weighTubeWithCuO: () => set((s) => {
    if (s.labStage !== 'cuo-added' || s.isWeighing) return {};
    return {
      isWeighing: true,
      weighProgress: 0,
      weighType: 'with-cuo',
      targetReadingG: 0,
    };
  }),

  buildApparatus: () => set((s) => {
    if (s.labStage !== 'cuo-weighed' || s.isBuildingApparatus) return {};
    return { isBuildingApparatus: true, buildProgress: 0 };
  }),

  connectApparatus: () => set((s) => {
    if (s.labStage !== 'apparatus-built' || s.isConnecting) return {};
    return { isConnecting: true, connectProgress: 0 };
  }),

  startH2Flow: () => set((s) => {
    if (s.labStage !== 'connected' || s.isH2Flowing) return {};
    return {
      isH2Flowing: true,
      labStage: 'purging',
      purgeSeconds: 0,
    };
  }),

  toggleH2Flow: () => set((s) => {
    // The H₂ flow is toggleable after the apparatus is connected.
    if (!s.connected) return {};
    return { isH2Flowing: !s.isH2Flowing };
  }),

  toggleHeating: () => set((s) => {
    // Safety: cannot light the lamp until purge is complete
    if (!s.isHeating && (s.labStage === 'purging' || s.labStage === 'connected') && s.purgeSeconds < PURGE_REQUIRED_SECONDS) {
      return {};
    }
    if (s.labStage === 'purging' && !s.isHeating) {
      return { isHeating: true, labStage: 'heating' };
    }
    if (s.labStage === 'heating' || s.labStage === 'reducing') {
      // Stop heating begins cooling
      return {
        isHeating: !s.isHeating,
        labStage: s.isHeating ? 'cooling' : s.labStage,
      };
    }
    return { isHeating: !s.isHeating };
  }),

  setFlameIntensity: (i) => set({ flameIntensity: Math.max(0, Math.min(1, i)) }),

  applyCottonBud: () => set((s) => {
    if (s.labStage !== 'cooling' || s.tubeTempC > 35 || s.isApplyingCottonBud) return {};
    return { isApplyingCottonBud: true, cottonProgress: 0 };
  }),

  weighFinal: () => set((s) => {
    if (s.labStage !== 'dried' || s.isWeighing) return {};
    return {
      isWeighing: true,
      weighProgress: 0,
      weighType: 'final',
      targetReadingG: 0,
    };
  }),

  checkConstantMass: () => set((s) => {
    if (s.labStage !== 'final-weighed' || s.isReheating) return {};
    if (s.reheatCycle >= 1) {
      return { labStage: 'constant-mass' };
    }
    return { isReheating: true, reheatProgress: 0 };
  }),

  recordObservation: () => set((s) => {
    if (s.labStage !== 'constant-mass') return {};
    const preset = TRIAL_PRESETS[s.currentTrial];
    const cuOMassG = preset.cuoMassG;
    const theoreticalCu = cuOMassG * (64 / 80);
    // Re-oxidation pushes recorded Cu mass back UP from theoretical
    const cappedReox = Math.min(s.h2StoppedEarlyG, REOXIDATION_CAP_G);
    const finalCuMassG = theoreticalCu + cappedReox;
    const oxygenLostG = Math.max(0, cuOMassG - finalCuMassG);
    const molesCu = finalCuMassG / 64;
    const molesO = oxygenLostG / 16;
    const ratio = molesCu > 0 ? molesO / molesCu : 0;
    const ratioRounded = Math.round(ratio * 100) / 100;
    const empiricalFormula =
      ratioRounded >= 0.97 && ratioRounded <= 1.03
        ? 'CuO'
        : `CuO${ratioRounded.toFixed(2)}`;
    const result: TrialResult = {
      trialId: s.currentTrial,
      emptyTubeMassG: parseFloat(EMPTY_TUBE_MASS_G.toFixed(2)),
      tubeWithCuOMassG: parseFloat((EMPTY_TUBE_MASS_G + cuOMassG).toFixed(2)),
      tubeWithCuMassG: parseFloat((EMPTY_TUBE_MASS_G + finalCuMassG).toFixed(2)),
      cuOMassG: parseFloat(cuOMassG.toFixed(2)),
      cuMassG: parseFloat(finalCuMassG.toFixed(3)),
      oxygenLostG: parseFloat(oxygenLostG.toFixed(3)),
      molesCu: parseFloat(molesCu.toFixed(4)),
      molesO: parseFloat(molesO.toFixed(4)),
      ratioCuToO: parseFloat(ratioRounded.toFixed(2)),
      empiricalFormula,
      recordedAt: Math.floor(s.experimentTime),
    };
    const allResults = [...s.results.filter(r => r.trialId !== s.currentTrial), result];
    const completed = allResults.length === 3;
    return {
      results: allResults,
      labStage: completed ? 'completed' : 'observed',
    };
  }),

  resetTrial: () => {
    const id = get().currentTrial;
    get().selectTrial(id);
  },

  resetAll: () => set({
    ...buildInitialTrialState(1),
    results: [],
    simulationSpeed: 1,
  }),

  setSimulationSpeed: (s) => set({ simulationSpeed: Math.max(1, Math.min(10, s)) }),

  cycleSimulationSpeed: () => set((state) => {
    const idx = SPEED_CYCLE.indexOf(state.simulationSpeed);
    const next = SPEED_CYCLE[(idx + 1) % SPEED_CYCLE.length];
    return { simulationSpeed: next };
  }),

  setCameraView: (v) => set((s) => ({ cameraView: v, cameraVersion: s.cameraVersion + 1 })),
  setShowTutorial: (show) => set({ showTutorial: show }),

  tick: (delta) => set((s) => {
    // Animations run in real time so they remain visible at 10× sim speed.
    const realDelta = delta / Math.max(0.0001, s.simulationSpeed);
    const out: Partial<CuOState> = {};

    out.experimentTime = s.experimentTime + delta;

    // Smoothly settle the balance reading
    if (Math.abs(s.currentReadingG - s.targetReadingG) > 0.001) {
      out.currentReadingG = s.currentReadingG + (s.targetReadingG - s.currentReadingG) * Math.min(1, 4 * delta);
    } else if (s.currentReadingG !== s.targetReadingG) {
      out.currentReadingG = s.targetReadingG;
    }

    // Weighing animation
    if (s.isWeighing && s.weighType) {
      const p = Math.min(1, s.weighProgress + realDelta / WEIGH_DURATION);
      const preset = TRIAL_PRESETS[s.currentTrial];

      // Pan empty as tube lifts off
      if (s.weighProgress < 0.05 && p >= 0.05) {
        out.targetReadingG = 0;
      }

      // Reading appears when the tube touches the pan
      if (s.weighProgress < 0.45 && p >= 0.45) {
        if (s.weighType === 'empty') {
          out.targetReadingG = EMPTY_TUBE_MASS_G;
        } else if (s.weighType === 'with-cuo') {
          out.targetReadingG = EMPTY_TUBE_MASS_G + preset.cuoMassG;
        } else {
          const theoreticalCu = preset.cuoMassG * (64 / 80);
          const cappedReox = Math.min(s.h2StoppedEarlyG, REOXIDATION_CAP_G);
          const finalTotalG = EMPTY_TUBE_MASS_G + theoreticalCu + cappedReox;
          out.targetReadingG = finalTotalG;
          out.lastWeighingG = finalTotalG;
        }
      }

      if (p >= 1) {
        out.isWeighing = false;
        out.weighProgress = 1;
        out.weighType = null;
        if (s.weighType === 'empty') {
          out.labStage = 'tube-weighed';
        } else if (s.weighType === 'with-cuo') {
          out.cuOInTubeG = TRIAL_PRESETS[s.currentTrial].cuoMassG;
          out.labStage = 'cuo-weighed';
        } else {
          out.labStage = 'final-weighed';
        }
      } else {
        out.weighProgress = p;
      }
    }

    // CuO placement animation
    if (s.isPlacingCuO) {
      const p = Math.min(1, s.placeCuOProgress + realDelta / PLACE_CUO_DURATION);
      const preset = TRIAL_PRESETS[s.currentTrial];
      const massInTube = preset.cuoMassG * Math.max(0, (p - 0.5) / 0.5);
      if (p >= 1) {
        out.isPlacingCuO = false;
        out.placeCuOProgress = 1;
        out.cuOInTubeG = preset.cuoMassG;
        out.labStage = 'cuo-added';
      } else {
        out.placeCuOProgress = p;
        out.cuOInTubeG = massInTube;
      }
    }

    // Apparatus build animation (water → Zn → HCl, sequenced)
    if (s.isBuildingApparatus) {
      const p = Math.min(1, s.buildProgress + realDelta / BUILD_DURATION);
      // Phase 1 (0..0.33): water pour
      if (s.buildProgress < 0.33 && p >= 0.33) {
        out.waterAdded = true;
      }
      // Phase 2 (0.33..0.66): zinc granules drop
      if (s.buildProgress < 0.66 && p >= 0.66) {
        out.znAdded = true;
      }
      // Phase 3 (0.66..1.0): HCl pour and rubber stoppers in
      if (p >= 1) {
        out.isBuildingApparatus = false;
        out.buildProgress = 1;
        out.hclAdded = true;
        out.apparatusBuilt = true;
        out.labStage = 'apparatus-built';
      } else {
        out.buildProgress = p;
      }
    }

    // Connect rubber tubing animation
    if (s.isConnecting) {
      const p = Math.min(1, s.connectProgress + realDelta / CONNECT_DURATION);
      if (p >= 1) {
        out.isConnecting = false;
        out.connectProgress = 1;
        out.connected = true;
        out.labStage = 'connected';
      } else {
        out.connectProgress = p;
      }
    }

    // Cotton-bud animation
    if (s.isApplyingCottonBud) {
      const p = Math.min(1, s.cottonProgress + realDelta / COTTON_DURATION);
      if (p >= 1) {
        out.isApplyingCottonBud = false;
        out.cottonProgress = 1;
        out.cottonApplied = true;
        out.labStage = 'dried';
      } else {
        out.cottonProgress = p;
      }
    }

    // Purge counter
    if (s.labStage === 'purging' && s.isH2Flowing && !s.isHeating) {
      out.purgeSeconds = s.purgeSeconds + delta;
    }

    // Heating / cooling temperature dynamics
    let nextTemp = s.tubeTempC;
    if (s.isHeating) {
      const target = 200 + s.flameIntensity * (TUBE_MAX_HEAT_C - 200);
      nextTemp += (target - s.tubeTempC) * 0.30 * delta;
      if (nextTemp > TUBE_MAX_HEAT_C) nextTemp = TUBE_MAX_HEAT_C;
    } else {
      nextTemp -= Math.max(0, (s.tubeTempC - 25)) * 0.30 * delta;
      if (nextTemp < 25) nextTemp = 25;
    }
    out.tubeTempC = nextTemp;

    // Heating → reducing transition once ignition temp is reached
    if (s.labStage === 'heating' && s.isHeating && nextTemp > IGNITION_TEMP_C) {
      out.labStage = 'reducing';
    }

    // Reduction progression
    if (s.labStage === 'reducing' && nextTemp > IGNITION_TEMP_C && s.isH2Flowing) {
      const preset = TRIAL_PRESETS[s.currentTrial];
      const theoreticalCu = preset.cuoMassG * (64 / 80);
      const nextProgress = Math.min(1, s.reductionProgress + delta / REDUCTION_DURATION);
      out.reductionProgress = nextProgress;
      out.cuOInTubeG = preset.cuoMassG * (1 - nextProgress);
      out.cuInTubeG = theoreticalCu * nextProgress;
    }

    // Re-oxidation penalty: H₂ stopped while still hot
    if (s.labStage === 'cooling' && !s.isH2Flowing && nextTemp > 80) {
      const reox = Math.min(REOXIDATION_CAP_G - s.h2StoppedEarlyG, REOXIDATION_RATE_G_PER_S * delta);
      if (reox > 0) {
        out.h2StoppedEarlyG = s.h2StoppedEarlyG + reox;
        // Visually drift the reduction back down a touch
        out.reductionProgress = Math.max(0.45, s.reductionProgress - reox * 0.8);
      }
    }

    // Reheat cycle (compressed visual loop)
    if (s.isReheating) {
      const p = Math.min(1, s.reheatProgress + delta / REHEAT_DURATION);
      const reheatTemp = p < 0.5
        ? 25 + (TUBE_MAX_HEAT_C - 25) * (p / 0.5)
        : TUBE_MAX_HEAT_C - (TUBE_MAX_HEAT_C - 25) * ((p - 0.5) / 0.5);
      out.tubeTempC = reheatTemp;
      if (p >= 1) {
        out.isReheating = false;
        out.reheatProgress = 1;
        out.reheatCycle = s.reheatCycle + 1;
        out.tubeTempC = 25;
        out.labStage = 'constant-mass';
      } else {
        out.reheatProgress = p;
      }
    }

    return out;
  }),
}));
